import Vue, {ComponentOptions} from 'vue'
import {RawLocation, Route, RouteRecord} from 'vue-router'
import {Store} from 'vuex'
import getFileName from './get-file-name'
import {Context} from './types'

/**
 * to resolve the hook in the vue-router navigation guard function
 * @link https://router.vuejs.org/guide/advanced/navigation-guards.html#navigation-guards
 */
export type Next<V extends Vue = Vue> = (
  to?: RawLocation | false | ((vm: V) => any) | undefined
) => void

export {Context}

export type RouterHook = (to: Route, from: Route, next?: Next) => any

export type RouterAfterHook = (to: Route, from: Route) => any

export interface AfterMiddlewareContext<A, S, V extends Vue = Vue> {
  to: Route
  from: Route
  next?: Next
  app: ComponentOptions<V> & A
  store?: Store<S>
}

export interface MiddlewareContext<A, S, V extends Vue = Vue>
  extends AfterMiddlewareContext<A, S, V> {
  next: Next
}

export interface MiddlewareInfo {
  name: string
  options?: any
}

export type Middleware<A, S, V extends Vue = Vue>
  = (context: MiddlewareContext<A, S, V>, options?: any) => any

export interface MiddlewarePack<A, S, V extends Vue = Vue> {
  name: string
  middleware: Middleware<A, S, V>
}

export interface MiddlewarePackList<A, S, V extends Vue = Vue> {
  beforeEnter: Array<MiddlewarePack<A, S, V>>
  beforeResolve: Array<MiddlewarePack<A, S, V>>
  afterEach: Array<MiddlewarePack<A, S, V>>
}

export interface ModulePack {
  name: string
  module: any
}

const createPack =
  <A, S, V extends Vue = Vue>(
    name: string,
    middleware: Middleware<A, S, V>,
  ): MiddlewarePack<A, S, V> => {

    return {
      name: getFileName(name),
      middleware,
    }
  }

export const getFileModule = (): ModulePack[] => {
  if(!process.env.VUE_APP_MIDDLEWARE_PATH) {
    return []
  }
  const resources: __WebpackModuleApi.RequireContext = require.context(
    `@/${process.env.VUE_APP_MIDDLEWARE_PATH}/`,
    false,
    /\.ts$/,
  )
  const keys = resources.keys()
  return keys.map((key) => ({
    name: key,
    module: resources(key),
  }))
}

export const getMiddleware = <A, S, V extends Vue = Vue>
(modulePack: ModulePack[]): MiddlewarePackList<A, S, V> => {
  const afterEachList: Array<MiddlewarePack<A, S, V>> = []
  const beforeEnterList: Array<MiddlewarePack<A, S, V>> = []
  const beforeResolveList: Array<MiddlewarePack<A, S, V>> = []
  modulePack.forEach(({module, name}: ModulePack) => {
    const {
      beforeEnter,
      beforeResolve,
      afterEach,
    } = module
    if(beforeEnter) {
      beforeEnterList.push(createPack(name, beforeEnter))
    }
    if(beforeResolve) {
      beforeResolveList.push(createPack(name, beforeResolve))
    }
    if(afterEach) {
      afterEachList.push(createPack(name, afterEach))
    }
  })
  return {
    beforeEnter: beforeEnterList,
    beforeResolve: beforeResolveList,
    afterEach: afterEachList,
  }
}

export interface Options<A, S, V extends Vue>
  extends CapsuleOptions, Partial<MiddlewarePackList<A, S, V>> {
}

export interface CapsuleOptions {
  always?: string[]
  allowDuplication?: boolean
  modulePacks?: ModulePack[]
}

/**
 * Return middleware option or empty option
 * @param middleware
 * @param name
 */
const hasMiddleware = (middleware: string | MiddlewareInfo, name: string): any | undefined => {
  if(typeof middleware === 'object') {
    if(middleware.name === name) {
      return middleware.options || {}
    }
    return
  }
  if(middleware === name) {
    return {}
  }
}

/**
 * Get middleware option in one of route recode
 * @param record
 * @param name middleware name
 */
const getMidOps = (record: RouteRecord, name): any | undefined => {
  if(!(record.meta && record.meta.middleware)) {
    return
  }
  const {middleware} = record.meta
  if(Array.isArray(middleware)) {
    for(const mid of middleware) {
      const result = hasMiddleware(mid, name)
      if(result) {
        return result
      }
    }
    return
  }
  return hasMiddleware(middleware, name)
}

/**
 * Get middleware options in route recode list
 * @param records record list from route matched
 * @param name middleware name
 */
const getMidOpsAll = (records: RouteRecord[], name: string): any[] => {
  return records.reduce((result: any[], record) => {
    const options = getMidOps(record, name)
    if(options) {
      result.push(options)
    }
    return result
  }, [])
}

/**
 * Get a last item in the list
 * @param list
 */
const last = (list: any[]) => {
  if(list.length < 1) {
    return
  }
  return list[list.length - 1]
}

/**
 * Runs middleware if route has a middleware name in to.matched list
 * @param name middleware name
 * @param middleware middleware recode
 * @param store vuex store
 * @param app new Vue() options
 * @param options middleware options
 */
const capsule = <A, S, V extends Vue = Vue>(
  name: string,
  middleware: Middleware<A, S, V>,
  store: Store<S>,
  app: ComponentOptions<V> & A,
  options: CapsuleOptions = {},
): RouterHook | RouterAfterHook => {
  const {
    always = [],
    allowDuplication = false,
  } = options
  // middleware part
  return (to: Route, from: Route, next?: Next) => {
    const runMiddleware = (options?: any) => {
      const ctx: any = {to, from, store, app}
      if(next) {
        ctx.next = next
      }
      return middleware(ctx, options)
    }
    const alwaysSome = (requireName): boolean => (name === requireName)

    const middlewareOptions = getMidOpsAll(to.matched, name)
    if(middlewareOptions.length > 0) {
      
      // apply all middleware or not if there are many middleware
      if(allowDuplication) {
        middlewareOptions.forEach((options: any) => {
          runMiddleware(options)
        })
        return
      }
      
      // single running
      runMiddleware(last(middlewareOptions))
      return
    }

    // use return because run middleware once
    if(always.some(alwaysSome)) {
      return runMiddleware()
    }
    // skip
    if(next) {
      next()
    }
  }
}

/**
 * Vue-router middleware
 * @export default
 * @param context middleware context
 * @param options middleware options
 */
export default function middleware<A, S,
  V extends Vue = Vue, C extends Context<A, S, V> = Context<A, S, V>>(
  context: Context<A, S, V>,
  options: Options<A, S, V> = {},
) {
  const {
    beforeEnter = [],
    beforeResolve = [],
    afterEach = [],
    modulePacks: _modulePacks = [],
  } = options
  const {router, store, app} = context
  if(!router) {
    // eslint-disable-next-line no-console
    return console.warn('[middleware] no router')
  }

  const modulePack = [..._modulePacks, ...getFileModule()]

  const middlewareList: MiddlewarePackList<A, S, V> = getMiddleware<A, S, V>(modulePack)
  // add middleware in options
  middlewareList.beforeEnter.push(...beforeEnter)
  middlewareList.beforeResolve.push(...beforeResolve)
  middlewareList.afterEach.push(...afterEach)

  // Encapsulation
  middlewareList.beforeEnter.forEach(({name, middleware}: MiddlewarePack<A, S, V>) => {
    router.beforeEach(capsule(name, middleware, store, app, options))
  })
  middlewareList.beforeResolve.forEach(({name, middleware}: MiddlewarePack<A, S, V>) => {
    router.beforeResolve(capsule(name, middleware, store, app, options))
  })
  middlewareList.afterEach.forEach(({name, middleware}: MiddlewarePack<A, S, V>) => {
    router.afterEach(capsule(name, middleware, store, app, options))
  })
}
